import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { PinFill } from '../../../assets/icons'
import breakpoints from '../../../theme/breakpoints'
import { colors } from '../../../theme/palette'
import { baseButton, sortPlaceholder } from '../../../theme/reuseables'
import { Card, CardCell, Circle, CircleText } from '../../ui/Card'
import { Typography } from '../../ui/Typography/Typography'
import { Button } from '../../ui/Button/Button'
import { Workplace } from '../../../models/Workplace'
import { EditWorkplaceNotes } from '../../modules/notes/EditWorkplaceNotes'
import { Member } from '../../../models/Member'
import { getInitials } from '../../../utils/initials'
import { Favorite } from '../../ui/Favorite'
import { NoteButton } from '../../ui/NoteButton'

const useStyles = createUseStyles({
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
        },
        to: {
            opacity: 1,
        },
    },
    container: {
        padding: '0 1.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: '0',
            },
        },
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '5rem',
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
    topArea: {
        borderBottom: `1px solid ${colors.gray700}`,
        // TODO: remove this when this area has been developed.
        minHeight: '60px',
    },
    headerActions: {
        display: 'flex',
        flexGrow: 1,
        marginLeft: 'auto',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        flexShrink: 0,
    },
    sendingEmailSettings: {
        display: 'flex',
        padding: '.5rem 1rem',
        minHeight: '5rem',
        width: '100%',
        backgroundColor: colors.green200,
        color: colors.black,
        justifyContent: 'space-between',
        alignItems: 'center',
        '& span': {
            fontWeight: 700,
        },
        animation: '.5s $fadeIn',
    },
    notes: {
        paddingTop: '20px',
    },
    dialogButtonsContainer: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-around',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    modalButtons: {
        display: 'flex',
    },
    modalCheckbox: {
        display: 'flex',
        position: 'absolute',
        right: '20px',
        top: '20px',
    },
    numnerOfNotes: {
        display: 'flex',
        padding: '30px 0 90px',
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconWrapper: {
        position: 'relative',
    },
    noteContent: {
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    noteButton: {
        position: 'relative',
    },
    deleteButtons: {
        top: '0',
        right: '0',
        display: 'flex',
        zIndex: '1',
        position: 'absolute',
        flexWrap: 'wrap',
        transition: 'all .3s ease-in',
        justifyContent: 'end',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        background: 'rgba(255,255,255, .7)',
    },
    sortPlaceholder: {
        ...sortPlaceholder,
    },
    googleLink: {
        marginLeft: 'auto',
    },
    showMembers: {
        display: 'flex',
    },
    link: {
        fontWeight: '400',
        fontSize: '16px',
        textDecoration: 'underline',
        color: colors.black,
        '&:visited, &:active, &:focus': {
            color: colors.black,
        },
        '&:hover': {
            color: colors.green500,
        },
    },
    numberOfMembers: {
        fontSize: '16px',
        color: colors.black,
        marginRight: '20px',
    },
    contentMobile: {
        padding: '20px',
    },
    searchField: {
        width: '100%',
        marginRight: '20px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                width: 'auto',
                minWidth: '425px',
                marginRight: '20px',
            },
        },
    },
})

const gridTemplateColumns = '23% 20% auto 230px'
const gridTemplateColumnsMembers = '8% 92%'

type WorkplaceCardCellProps = {
    wp: Workplace
    allMembers: Member[]
}

const WorkplaceCardCell: FC<WorkplaceCardCellProps> = ({ wp, allMembers }) => {
    const classes = useStyles()
    const [membersForWorkplace, setMembersForWorkplace] = useState<Member[]>()
    const [open, setOpen] = useState(false)

    const onClickMapLink = (workplace: Workplace): void => {
        // eslint-disable-next-line max-len
        const link = `https://www.google.com/maps/search/?api=1&query=${workplace.address.addressLine1}+${workplace.address.addressLine2}+${workplace.address.addressLine3}%2C+${workplace.address.postNumber}+${workplace.address.postDistrict}`

        window.open(link, '_blank')
    }

    useEffect(() => {
        let numberOfMembers = []

        wp.trMemberList.forEach((item) => {
            numberOfMembers = [...numberOfMembers, ...item.Medlemsidenter]
        })

        const wpMembersIdents = Array.from(new Set(numberOfMembers))
        const wpMembers: Member[] = []
        wpMembersIdents.forEach((item) => {
            const found = allMembers.find((m) => m.memberId === item)

            if (found) {
                wpMembers.push(found)
            }
        })

        setMembersForWorkplace(wpMembers)
    }, [wp])
    return (
        <Card
            gridTemplateColumns={gridTemplateColumns}
            showFooter={open}
            footer={
                <>
                    <div
                        style={{
                            paddingLeft: '0',
                            paddingBottom: '10px',
                            width: 'auto',
                            borderBottom: `1px solid ${colors.borderColorLight}`,
                            margin: '0 20px 20px',
                        }}
                    >
                        <Typography size="S">
                            Medlemmer på arbejdssted:
                        </Typography>
                    </div>

                    {membersForWorkplace
                        ? membersForWorkplace.map((member) => {
                              return (
                                  <>
                                      <Card
                                          key={member.memberId}
                                          gridTemplateColumns={
                                              gridTemplateColumnsMembers
                                          }
                                          url={`/mine-medlemmer/${member.memberId}?backUrl=/mine-arbejdsteder`}
                                      >
                                          <CardCell>
                                              <div
                                                  className={
                                                      classes.iconWrapper
                                                  }
                                                  style={{
                                                      paddingLeft: '10px',
                                                  }}
                                              >
                                                  <Circle>
                                                      <CircleText>
                                                          {getInitials(
                                                              member.fullName
                                                          )}
                                                      </CircleText>
                                                  </Circle>
                                                  <Favorite
                                                      active={member.isFavorite}
                                                      right="-10px"
                                                  />
                                              </div>
                                          </CardCell>
                                          <CardCell>
                                              <div
                                                  style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                  }}
                                              >
                                                  <Typography size="XS">
                                                      {member.fullName}
                                                  </Typography>
                                                  <div
                                                      className={
                                                          classes.noteButton
                                                      }
                                                  >
                                                      <NoteButton>
                                                          {member.numberOfNotes}
                                                      </NoteButton>
                                                  </div>
                                              </div>
                                          </CardCell>
                                      </Card>
                                  </>
                              )
                          })
                        : null}
                </>
            }
        >
            <CardCell>
                <Typography size="XS">{wp.name}</Typography>
            </CardCell>
            <CardCell>
                <div className={classes.showMembers}>
                    <div className={classes.numberOfMembers}>
                        {membersForWorkplace && membersForWorkplace.length}{' '}
                    </div>

                    {membersForWorkplace && membersForWorkplace.length ? (
                        <Button
                            type="action"
                            style={{
                                fontWeight: 400,
                                fontSize: '16px',
                                textDecoration: 'underline',
                            }}
                            onClick={() => setOpen(!open)}
                        >
                            {!open ? 'Vis medlemmer' : 'Skjul medlemmer'}
                        </Button>
                    ) : null}
                </div>
            </CardCell>
            <CardCell>
                <div style={{ display: 'flex' }}>
                    <Typography size="XS">
                        {wp.address.addressLine1}, {wp.address.postNumber}{' '}
                        {wp.address.postDistrict}
                    </Typography>
                    <Button
                        type="action"
                        style={{
                            marginLeft: 'auto',
                            cursor: 'pointer',
                        }}
                        onClick={() => onClickMapLink(wp)}
                    >
                        <div style={{ display: 'flex' }}>
                            <Typography
                                size="XS"
                                style={{
                                    textDecoration: 'underline',
                                }}
                            >
                                Vis vej
                            </Typography>
                            <PinFill width="20px" color={colors.green500} />
                        </div>
                    </Button>
                </div>
            </CardCell>
            <CardCell>
                <EditWorkplaceNotes workplace={wp} />
            </CardCell>
        </Card>
    )
}

export { WorkplaceCardCell }
